import { getAdobeExperimentSSR } from '@cinch-nx/adobe-server-side'
import { getStoryBySlug } from '@cinch-nx/data-storyblok'
import { getTrustpilotReviews } from '@cinch-nx/data-trustpilot'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { setTag, tagError, traceFunction } from '@core/datadog-server'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'

import { homeStoryFallback } from './data/fallback/homepage-props-fallback'
import { getHomePageVehiclesData } from './data/vehicles'
import { getPersonalisationEligibility } from './helpers/get-personalisation-eligibility'
import { HomeStoryContent } from './types/home-story-content'

const EXPERIMENT_NAMES = [readFromEnv(Env.NavbarValuationExperimentId)]

async function getStory(preview: boolean) {
  const storyName = 'home'
  try {
    const story = await getStoryBySlug<HomeStoryContent>(storyName, {
      version: preview ? 'draft' : 'published',
      resolve_relations: 'CarsCarousel.financeRepresentativeExample',
    })
    return { story: story, usingStoryFallback: false }
  } catch (error) {
    tagError(
      `Error when fetching story '${storyName}' from storyblok. ${error}`,
    )
    return { story: homeStoryFallback, usingStoryFallback: true }
  }
}

async function getServerSidePropsfn(ctx: GetServerSidePropsContext) {
  const isInsideStoryblok = !!ctx.preview

  const [
    { vehiclesData, response: searchResponse },
    { story, usingStoryFallback },
    trustpilotData,
    { showPersonalisedComponents, similarListings, recentlyViewedListings },
    experiments,
  ] = await Promise.all([
    getHomePageVehiclesData(),
    getStory(isInsideStoryblok),
    getTrustpilotReviews(),
    getPersonalisationEligibility(ctx.req.cookies),
    getAdobeExperimentSSR(EXPERIMENT_NAMES, ctx),
  ])

  setTag('response.code', 200)
  setTag('page.name', 'homepage')
  setTag('page.url', ctx.resolvedUrl)
  setTag('page.rendered', 'SSR')
  setTag(
    'page.params',
    JSON.stringify(
      {
        preview: isInsideStoryblok,
        query: ctx.query,
        showPersonalisedComponents,
      },
      null,
      2,
    ),
  )

  return {
    props: {
      story,
      usingStoryFallback,
      vehiclesData,
      searchResponse,
      showPersonalisedComponents,
      trustpilotData,
      preview: isInsideStoryblok,
      recentlyViewedListings,
      similarListings,
      generatedAt: Date.now().toString(),
      pageMeta: {
        experiments,
      },
    },
  }
}

export const getServerSideProps = traceFunction(
  'homepage.getServerSideProps',
  getServerSidePropsfn,
)

// infer the type, but make searchResponse optional
export type HomepageProps = Omit<
  InferGetServerSidePropsType<typeof getServerSideProps>,
  'searchResponse'
> &
  Partial<
    Pick<
      InferGetServerSidePropsType<typeof getServerSideProps>,
      'searchResponse'
    >
  >
